import {gridFromData, gridToData} from '../models/polygon-grid';
import {Polygon} from '../models/polygon';
import {PolygonField} from '../enums/database/polygon-field.enum';
import {AltitudeMode3d} from '../enums/altitude-mode.enum';
import {PointField} from '../enums/database/point-field.enum';
import {PolygonPoint} from '../models/polygon-point';
import {SpotField} from '../enums/database/spot-field.enum';
import {Spot} from '../models/spot';
import {ParcelInfo} from '../models/parce-info';
import {LineStyle} from "../enums/line-style.enum";
import {BufferUtil} from './buffer-util';
import {SettingsService} from '../services/settings.service';

/**
 * The products a stored shape carries.
 *
 * `product_ids` is the whole list and `product_id` its first entry. Apps that
 * only know `product_id` change it alone, so the list only counts while it
 * still starts with it: a product changed or cleared by one of them wins over
 * a list it left behind.
 */
export function productsFromData(productId: any, productIds: any): string[] {
  const first = typeof productId === 'string' ? productId : '';
  const list = Array.isArray(productIds) ? productIds.filter(id => typeof id === 'string' && id) : [];
  if (list.length && list[0] === first) {
    return list;
  }
  return first ? [first] : [];
}

export class GeometryConverter {

  public static polygonToData(polygon: Polygon, withPoints: boolean = true) {
    const data = {};

    data[PolygonField.KEY_ID] = polygon.id;
    data[PolygonField.KEY_TYPE] = polygon.type;
    data[PolygonField.KEY_POLYGON_ID] = polygon.id;
    data[PolygonField.KEY_TITLE] = polygon.name;
    data[PolygonField.KEY_DESCRIPTION] = polygon.description ? polygon.description : '';
    data[PolygonField.KEY_SHAPE] = polygon.shape;
    data[PolygonField.KEY_NUM_POINTS] = polygon.getPointsCount();
    data[PolygonField.KEY_LINE_STYLE] = polygon.lineStyle;
    data[PolygonField.KEY_LINE_COLOR] = polygon.lineColor;
    data[PolygonField.KEY_LINE_WIDTH] = polygon.lineWidth;
    data[PolygonField.KEY_FILL_COLOR] = polygon.fillColor;
    data[PolygonField.KEY_FILL_OPACITY] = polygon.fillOpacity;
    data[PolygonField.KEY_TOTAL_AREA] = polygon.area;
    data[PolygonField.KEY_TOTAL_PERIMETER] = polygon.perimeter;
    data[PolygonField.KEY_IS_LOCKED] = polygon.isLocked;
    data[PolygonField.KEY_IS_HOLE] = polygon.isHole;
    data[PolygonField.KEY_SHOW_PINS] = polygon.showPins;
    data[PolygonField.KEY_SHOW_PINS_WHEN_UNLOCKED] = polygon.showPinsWhenUnlocked;
    data[PolygonField.KEY_NO_SURFACE] = !polygon.showSurface;
    data[PolygonField.KEY_SHOW_DISTANCES] = polygon.showMiddleDistances;
    data[PolygonField.KEY_SHOW_NAME] = polygon.showName;
    data[PolygonField.KEY_SHOW_AREA_MEASURE] = polygon.showArea && polygon.showSurface;
    data[PolygonField.KEY_SHOW_PERIM_MEASURE] = polygon.showPerimeter;
    data[PolygonField.KEY_SHOW_LABEL_BACKGROUND] = polygon.label.hasBackground;
    data[PolygonField.KEY_SHOW_PRICE_LABEL] = polygon.showPriceLabel;
    data[PolygonField.KEY_TEXTURE_ROTATION] = polygon.textureRotation || 0;
    data[PolygonField.KEY_ALTITUDE_MODE_3D] = polygon.altitudeMode3d || AltitudeMode3d.DEFAULT;
    data[PolygonField.KEY_ALTITUDE_3D] = polygon.altitude3d || 0;
    data[PolygonField.KEY_HEIGHT_OFFSET_3D] = polygon.heightOffset3d || 0;
    data[PolygonField.KEY_EXTRUDED_3D] = !!polygon.extruded3d;
    // Written even when there is none, so removing a grid is saved.
    data[PolygonField.KEY_GRID] = gridToData(polygon.grid);
    data[PolygonField.KEY_IS_BUFFER] = polygon.isBuffer;
    // if (polygon.parentId) {
    //   data[PolygonField.KEY_PARENT_ID] = polygon.parentId;
    // }
    data[PolygonField.KEY_PARENT_ID] = polygon.parentId ? polygon.parentId : '';
    data[PolygonField.KEY_GROUP_ID] = polygon.groupId ? polygon.groupId : '';
    // Only on a shape a buffer made, and still following its shape. Left out
    // otherwise: the document is written whole, so leaving it out clears it.
    if (polygon.bufferParams) {
      data[PolygonField.KEY_BUFFER] = BufferUtil.paramsToData(polygon.bufferParams);
    }

    if(polygon.parcelInfo && polygon.parcelInfo.providerId && polygon.parcelInfo.reference){
      data[PolygonField.KEY_PARCEL_PROVIDER_ID] = polygon.parcelInfo.providerId;
      data[PolygonField.KEY_PARCEL_REF] = polygon.parcelInfo.reference;
      data[PolygonField.KEY_PARCEL_EXTRA_INFO] = polygon.parcelInfo.extraInfo;
    }

    // Always write the field (even empty) — omitting it on an update left the
    // old product in the DB, so clearing a polygon's product ("None") didn't
    // persist and came back on reload.
    data[PolygonField.KEY_PRODUCT_ID] = polygon.productId || '';
    data[PolygonField.KEY_PRODUCT_IDS] = [...(polygon.productIds || [])];

    let titlePos;
    if (polygon.label) {
      titlePos = polygon.label.latLng;
    }

    if (!titlePos && polygon.getFirstPoint()) {
      titlePos = polygon.getFirstPoint().latLng;
    }
    if (!titlePos) {
      titlePos = {
        lat: 0,
        lng: 0
      };
    }
    data[PolygonField.KEY_TITLE_LABEL_LAT] = titlePos.lat;
    data[PolygonField.KEY_TITLE_LABEL_LON] = titlePos.lng;
    // Said only of a position placed by hand, which pins the label on the next
    // load; any other stays put until the corners change (Polygon.loadLabel).
    // Never false: the mobile apps move a label without knowing the flag and
    // leave it as it was, so a false would send their label back to the
    // middle. Left out, it is also removed from a shape that had it.
    if (polygon.titleLabelPlacedByHand()) {
      data[PolygonField.KEY_TITLE_LABEL_MANUAL] = true;
    }

    if (withPoints) {
      const pointsData = {};
      polygon.points.forEach(ap => {
        pointsData[ap.id] = this.pointToData(ap);
      });
      data['points'] = pointsData;
    }

    if (polygon.images && polygon.images.length > 0) {
      data[PolygonField.KEY_IMAGES] = polygon.images;
    } else {
      data[PolygonField.KEY_IMAGES] = [];
    }

    return data;
  }

  public static dataToPolygon(data): Polygon {
    const id = data[PolygonField.KEY_ID];
    const shape = data[PolygonField.KEY_SHAPE];
    const polygon = new Polygon(id, shape);
    this.loadDataToPolygon(polygon, data);
    return polygon;
  }

  /**
   * Loads a stored shape into a Polygon, new or existing.
   *
   * Loading into an existing polygon is how undo, redo and other people's
   * changes arrive, so everything the data describes is set -- including what
   * it leaves out. The shape (a rectangle that became a polygon when a vertex
   * was inserted) and the parcel link used to keep whatever the object had.
   */
  public static loadDataToPolygon(polygon: Polygon, data): Polygon {
    if (data[PolygonField.KEY_SHAPE] !== undefined && data[PolygonField.KEY_SHAPE] !== null) {
      polygon.shape = data[PolygonField.KEY_SHAPE];
    }
    polygon.name = data[PolygonField.KEY_TITLE];
    polygon.description = data[PolygonField.KEY_DESCRIPTION];
    polygon.lineStyle = data[PolygonField.KEY_LINE_STYLE] ? data[PolygonField.KEY_LINE_STYLE] : LineStyle.SOLID;
    polygon.lineColor = data[PolygonField.KEY_LINE_COLOR];
    polygon.lineWidth = data[PolygonField.KEY_LINE_WIDTH];
    polygon.fillColor = data[PolygonField.KEY_FILL_COLOR];
    polygon.fillOpacity = data[PolygonField.KEY_FILL_OPACITY];
    polygon.isLocked = data[PolygonField.KEY_IS_LOCKED];
    polygon.isHole = data[PolygonField.KEY_IS_HOLE];
    polygon.showPins = data[PolygonField.KEY_SHOW_PINS];
    polygon.showPinsWhenUnlocked = data[PolygonField.KEY_SHOW_PINS_WHEN_UNLOCKED];
    polygon.showSurface = !data[PolygonField.KEY_NO_SURFACE];
    polygon.showMiddleDistances = data[PolygonField.KEY_SHOW_DISTANCES];
    polygon.showName = data[PolygonField.KEY_SHOW_NAME] || data[PolygonField.KEY_SHOW_NAME] === undefined;
    polygon.showArea = data[PolygonField.KEY_SHOW_AREA_MEASURE];
    polygon.showPerimeter = data[PolygonField.KEY_SHOW_PERIM_MEASURE];
    polygon.label.hasBackground = !(data[PolygonField.KEY_SHOW_LABEL_BACKGROUND] === false);
    polygon.isBuffer = data[PolygonField.KEY_IS_BUFFER];
    polygon.bufferParams = BufferUtil.paramsFromData(data[PolygonField.KEY_BUFFER]);
    polygon.parentId = data[PolygonField.KEY_PARENT_ID];
    polygon.groupId = data[PolygonField.KEY_GROUP_ID];
    polygon.lastUserUpdateId = data[PolygonField.KEY_LAST_UPDATE_USER];
    polygon.setProducts(productsFromData(data[PolygonField.KEY_PRODUCT_ID], data[PolygonField.KEY_PRODUCT_IDS]));
    polygon.showPriceLabel = data[PolygonField.KEY_SHOW_PRICE_LABEL] ? data[PolygonField.KEY_SHOW_PRICE_LABEL] : false;
    polygon.textureRotation = data[PolygonField.KEY_TEXTURE_ROTATION] || 0;
    polygon.altitudeMode3d = data[PolygonField.KEY_ALTITUDE_MODE_3D] || AltitudeMode3d.DEFAULT;
    polygon.altitude3d = data[PolygonField.KEY_ALTITUDE_3D] || 0;
    polygon.heightOffset3d = data[PolygonField.KEY_HEIGHT_OFFSET_3D] || 0;
    fromFixedHeight(polygon, SettingsService.localSettings?.default3dAltitude);
    polygon.extruded3d = !!data[PolygonField.KEY_EXTRUDED_3D];
    polygon.grid = gridFromData(data[PolygonField.KEY_GRID]);

    if(data[PolygonField.KEY_PARCEL_PROVIDER_ID] && data[PolygonField.KEY_PARCEL_REF]){
      polygon.parcelInfo = new ParcelInfo();
      polygon.parcelInfo.providerId = data[PolygonField.KEY_PARCEL_PROVIDER_ID];
      polygon.parcelInfo.reference = data[PolygonField.KEY_PARCEL_REF];
      polygon.parcelInfo.extraInfo = data[PolygonField.KEY_PARCEL_EXTRA_INFO];
    } else {
      polygon.parcelInfo = null;
    }


    const titlePos = {
      lat: data[PolygonField.KEY_TITLE_LABEL_LAT],
      lng: data[PolygonField.KEY_TITLE_LABEL_LON]
    };

    if (data.points) {
      let pointsData = data.points;
      let points: PolygonPoint[] = [];
      if (!Array.isArray(pointsData)) {
        pointsData = Object.values(pointsData);
      }
      // The vertices already on the polygon are reloaded in place rather than
      // replaced. Panels, selections and the crosshair hold on to vertex
      // objects, and every undo, redo or remote change used to swap them all
      // for new ones, leaving those holding a copy nobody drew.
      const existing = new Map<string, PolygonPoint>();
      (polygon.points || []).forEach(p => existing.set(p.id, p));
      pointsData.forEach(pointData => {
        const reused = existing.get(pointData[PointField.KEY_ID]);
        if (reused) {
          reused.parent = polygon;
          this.loadDataToPoint(reused, pointData);
          points.push(reused);
        } else {
          points.push(this.dataToPoint(polygon, pointData));
        }
      })

      points = points.sort((p1, p2) => {
        if (p1.index < p2.index) {
          return -1;
        }
        if (p1.index > p2.index) {
          return 1;
        }
        return 0;
      });
      polygon.points = points;
    }

    let images = data[PolygonField.KEY_IMAGES];
    if (images) {
      if (!Array.isArray(images)) {
        images = images.split('|');
      }
      polygon.images = images;
    }

    polygon.updatePath();
    polygon.calculateDistanceLabels();
    polygon.recalculateStats();
    // Not updateLabel(titlePos): that pins the label as if dragged there, and
    // every automatic label then stayed behind when the shape was edited.
    polygon.loadLabel(titlePos, data[PolygonField.KEY_TITLE_LABEL_MANUAL]);

    return polygon;
  }

  public static pointToData(point: PolygonPoint) {
    const data = {};

    const descPos = point.getDescriptionPosition();

    data[PointField.KEY_INDEX] = point.getIndex();
    data[PointField.KEY_ID] = point.id;
    data[PointField.KEY_POINT_ID] = point.id;
    if (point.next) {
      data[PointField.KEY_NEXT_POINT_ID] = point.next.id;
    }
    if (point.prev) {
      data[PointField.KEY_PREV_POINT_ID] = point.prev.id;
    }
    data[PointField.KEY_TYPE] = point.type;
    data[PointField.KEY_POLYGON_ID] = point.getParentId();
    data[PointField.KEY_LAT] = point.lat;
    data[PointField.KEY_LONG] = point.lng;
    // Written independently. The 3D height used to be saved only alongside a
    // sea-level altitude, and moving a vertex resets that to 0, so moving it
    // (or redoing a move) quietly dropped its height.
    if (point.altitude && !isNaN(point.altitude)) {
      data[PointField.KEY_ALT] = point.altitude;
    }
    if (point.altitude3d && !isNaN(point.altitude3d)) {
      data[PointField.KEY_ALT_3D] = point.altitude3d;
    }
    data[PointField.KEY_DESCRIPTION] = point.description;
    data[PointField.KEY_DESC_LAT] = descPos ? descPos.lat : point.lat;
    data[PointField.KEY_DESC_LONG] = descPos ? descPos.lng : point.lng;
    data[PointField.KEY_SHOW_IN_REPORT] = point.showInReport;
    data[PointField.KEY_SHOW_DESC] = point.showLabelOnMap;
    data[PointField.KEY_SHOW_LABEL_BACKGROUND] = point.label.hasBackground;

    if (point.images && point.images.length > 0) {
      data[PointField.KEY_IMAGES] = point.images;
    } else {
      data[PointField.KEY_IMAGES] = [];
    }

    return data;
  }

  public static dataToPoint(polygon: Polygon, data): PolygonPoint {
    const id = data[PointField.KEY_ID];
    const point = new PolygonPoint(id, polygon, {lat: 0, lng: 0});
    this.loadDataToPoint(point, data);
    return point;
  }

  public static loadDataToPoint(point: PolygonPoint, data: any) {
    point.index = data[PointField.KEY_INDEX];
    point.latLng = {
      lat: data[PointField.KEY_LAT],
      lng: data[PointField.KEY_LONG]
    };
    point.altitude = data[PointField.KEY_ALT] ? data[PointField.KEY_ALT] : 0.0;
    point.altitude3d = data[PointField.KEY_ALT_3D] ? data[PointField.KEY_ALT_3D] : 0.0;
    point.description = data[PointField.KEY_DESCRIPTION];
    point.showInReport = data[PointField.KEY_SHOW_IN_REPORT];
    point.showLabelOnMap = data[PointField.KEY_SHOW_DESC];
    point.label.hasBackground = !(data[PointField.KEY_SHOW_LABEL_BACKGROUND] === false);
    point.updateLabel({
      lat: data[PointField.KEY_DESC_LAT],
      lng: data[PointField.KEY_DESC_LONG]
    });

    // if (point.next) {
    //   data[PointField.KEY_NEXT_POINT_ID] = point.next.id;
    // }
    // if (point.prev) {
    //   data[PointField.KEY_PREV_POINT_ID] = point.prev.id;
    // }

    let imgs = data[PointField.KEY_IMAGES];
    if (imgs) {
      if (!Array.isArray(imgs)) {
        imgs = imgs.split('|');
      }
      point.images = imgs;
    }
  }

  public static spotToData(spot: Spot): any {
    const data = {};

    const descPos = spot.getDescriptionPosition();

    data[SpotField.KEY_NAME] = spot.name;
    data[SpotField.KEY_TYPE] = spot.type;
    data[SpotField.KEY_SHOW_NAME] = spot.showLabelOnMap;
    data[SpotField.KEY_ID] = spot.id;
    data[SpotField.KEY_SPOT_ID] = spot.id;
    data[SpotField.KEY_ICON_ID] = spot.iconId;
    data[SpotField.KEY_ICON_COLOR] = spot.iconColor;
    data[SpotField.KEY_LAT] = spot.latLng.lat;
    data[SpotField.KEY_LONG] = spot.latLng.lng;
    data[SpotField.KEY_ALT] = spot.altitude ? spot.altitude : 0.0;
    data[SpotField.KEY_ALT_3D] = spot.altitude3d ? spot.altitude3d : 0.0;
    data[SpotField.KEY_DESCRIPTION] = spot.description;
    data[SpotField.KEY_DESC_LAT] = descPos ? descPos.lat : spot.lat;
    data[SpotField.KEY_DESC_LONG] = descPos ? descPos.lng : spot.lng;
    data[SpotField.KEY_SHOW_IN_REPORT] = spot.showInReport;
    data[SpotField.KEY_SHOW_LABEL_BACKGROUND] = spot.label.hasBackground;
    data[SpotField.KEY_GROUP_ID] = spot.groupId;

    // Always write (even empty) so clearing a spot's product persists.
    data[SpotField.KEY_PRODUCT_ID] = spot.productId || '';
    data[SpotField.KEY_PRODUCT_IDS] = [...(spot.productIds || [])];

    if (spot.images && spot.images.length > 0) {
      data[SpotField.KEY_IMAGES] = spot.images;
    } else {
      data[SpotField.KEY_IMAGES] = [];
    }

    return data;
  }

  public static dataToSpot(data): Spot {
    const id = data[SpotField.KEY_ID];
    const spot = new Spot(id);
    this.loadDataToSpot(spot, data);
    return spot;
  }

  public static loadDataToSpot(spot: Spot, data) {
    spot.latLng = {
      lat: data[SpotField.KEY_LAT],
      lng: data[SpotField.KEY_LONG]
    };
    spot.altitude = data[SpotField.KEY_ALT] ? data[SpotField.KEY_ALT] : 0.0;
    spot.altitude3d = data[SpotField.KEY_ALT_3D] ? data[SpotField.KEY_ALT_3D] : 0.0;
    // Always a whole number: it becomes part of an image address, and the
    // stored field is whatever whoever wrote the document put there.
    const iconId = Number(data[SpotField.KEY_ICON_ID]);
    spot.iconId = Number.isInteger(iconId) && iconId >= 0 ? iconId : 0;
    spot.groupId = data[SpotField.KEY_GROUP_ID];
    spot.iconColor = data[SpotField.KEY_ICON_COLOR];
    spot.name = data[SpotField.KEY_NAME];
    spot.description = data[SpotField.KEY_DESCRIPTION];
    spot.showLabelOnMap = data[SpotField.KEY_SHOW_NAME];
    spot.label.hasBackground = !(data[SpotField.KEY_SHOW_LABEL_BACKGROUND] === false);
    spot.updateLabel({
      lat: data[SpotField.KEY_DESC_LAT],
      lng: data[SpotField.KEY_DESC_LONG]
    });
    spot.showInReport = data[SpotField.KEY_SHOW_IN_REPORT];
    spot.setProducts(productsFromData(data[SpotField.KEY_PRODUCT_ID], data[SpotField.KEY_PRODUCT_IDS]));

    let imgs = data[SpotField.KEY_IMAGES];
    if (imgs) {
      if (!Array.isArray(imgs)) {
        imgs = imgs.split('|');
      }
      spot.images = imgs;
    }
  }

}

/**
 * "At a fixed height" is read as what it always was: On the ground, raised by
 * that height (both draw RELATIVE_TO_GROUND; the fixed one took its height
 * from `altitude3d`, or else this device's default height). The menu no
 * longer offers it, since Raise or lower already says the same (user,
 * 2026-10-25), so a shape saved with it -- or imported from an .mmp file --
 * opens as On the ground with its height there and stays where it was
 * drawn. Written back that way the next time the shape is saved.
 */
export function fromFixedHeight(polygon: Polygon, defaultHeight: number = 0): void {
  if (polygon.altitudeMode3d !== AltitudeMode3d.FIXED) {
    return;
  }
  const height = polygon.altitude3d > 0 ? polygon.altitude3d : Number(defaultHeight) || 0;
  polygon.altitudeMode3d = AltitudeMode3d.GROUND;
  polygon.heightOffset3d = (Number(polygon.heightOffset3d) || 0) + Math.max(0, height);
  polygon.altitude3d = 0;
}
